import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/apiService';
import { Plus, CreditCard, TrendingUp, TrendingDown } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

interface CreditBalance {
  balance: number;
  totalUsed: number;
  lastActivity: string | null;
}

interface Transaction {
  id: string;
  amount: number;
  type: 'topup' | 'usage' | 'refund' | 'adjustment';
  description: string;
  timestamp: string;
  apiKeyId?: string;
}

export default function Credits() {
  const { user } = useAuth();
  const [balance, setBalance] = useState<CreditBalance | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showTopUpModal, setShowTopUpModal] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState('');
  const [topUpCurrency, setTopUpCurrency] = useState('USD');
  const [topUpDescription, setTopUpDescription] = useState('');
  const [topUpLoading, setTopUpLoading] = useState(false);
  const [showManualTopUp, setShowManualTopUp] = useState(false);

  // Check if user is admin
  const isAdmin = user?.isAdmin || user?.email?.endsWith('@fenxlabs.ai');

  useEffect(() => {
    loadData();
    // Handle URL parameters for payment status
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success') === 'true') {
      setError('');
      // Show success message and reload data
      setTimeout(() => {
        loadData();
        window.history.replaceState({}, '', '/portal/credits');
      }, 1000);
    } else if (urlParams.get('canceled') === 'true') {
      setError('Payment was canceled');
      setTimeout(() => {
        setError('');
        window.history.replaceState({}, '', '/portal/credits');
      }, 3000);
    }
  }, []);

  const loadData = async () => {
    try {
      const [balanceData, transactionData] = await Promise.all([
        apiService.getCreditBalance(),
        apiService.getCreditHistory(50, 0)
      ]);
      setBalance(balanceData);
      setTransactions(transactionData);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTopUp = async () => {
    const amount = parseFloat(topUpAmount);
    if (!amount || amount < 5 || amount > 100) {
      setError('Amount must be between 5 and 100');
      return;
    }

    setTopUpLoading(true);
    try {
      const result = await apiService.createCreditCheckout(amount, topUpCurrency);
      // Redirect to Stripe checkout
      window.location.href = result.checkoutUrl;
    } catch (err: any) {
      setError(err.message);
      setTopUpLoading(false);
    }
  };

  const handleManualTopUp = async () => {
    const amount = parseFloat(topUpAmount);
    if (!amount || amount <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    setTopUpLoading(true);
    try {
      await apiService.topUpCredits(amount, topUpDescription || undefined);
      setShowTopUpModal(false);
      setTopUpAmount('');
      setTopUpDescription('');
      await loadData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setTopUpLoading(false);
    }
  };

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'topup':
      case 'refund':
        return <TrendingUp className="h-5 w-5 text-green-500" />;
      case 'usage':
        return <TrendingDown className="h-5 w-5 text-red-500" />;
      default:
        return <CreditCard className="h-5 w-5 text-gray-500" />;
    }
  };

  const getTransactionColor = (type: string, amount: number) => {
    if (amount > 0) return 'text-green-600';
    return 'text-red-600';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Credits</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage your credit balance and view transaction history
          </p>
        </div>
        <button
          onClick={() => setShowTopUpModal(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Credits
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Balance Card */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 overflow-hidden shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CreditCard className="h-8 w-8 text-white" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-indigo-200 truncate">
                  Current Balance
                </dt>
                <dd className="text-3xl font-bold text-white">
                  ${balance?.balance?.toFixed(4) || '0.0000'}
                </dd>
              </dl>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div>
              <dt className="text-sm font-medium text-indigo-200">
                Total Used
              </dt>
              <dd className="text-lg font-medium text-white">
                ${balance?.totalUsed?.toFixed(4) || '0.0000'}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-indigo-200">
                Last Activity
              </dt>
              <dd className="text-lg font-medium text-white">
                {balance?.lastActivity 
                  ? new Date(balance.lastActivity).toLocaleDateString()
                  : 'Never'
                }
              </dd>
            </div>
          </div>
        </div>
      </div>

      {/* Top-up Modal */}
      {showTopUpModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Add Credits
              </h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Amount
                    </label>
                    <input
                      type="number"
                      step="1"
                      min="5"
                      max="100"
                      placeholder="Enter amount"
                      value={topUpAmount}
                      onChange={(e) => setTopUpAmount(e.target.value)}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Currency
                    </label>
                    <select
                      value={topUpCurrency}
                      onChange={(e) => setTopUpCurrency(e.target.value)}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="USD">USD ($)</option>
                      <option value="EUR">EUR (€)</option>
                      <option value="GBP">GBP (£)</option>
                    </select>
                  </div>
                </div>
                <div className="text-sm text-gray-500">
                  Min: {topUpCurrency} 5 | Max: {topUpCurrency} 100
                </div>
                {showManualTopUp && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Description (optional)
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., Testing credits"
                      value={topUpDescription}
                      onChange={(e) => setTopUpDescription(e.target.value)}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                )}
              </div>
              <div className="mt-6 space-y-3">
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setShowTopUpModal(false)}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleTopUp}
                    disabled={topUpLoading || !topUpAmount || parseFloat(topUpAmount) < 5 || parseFloat(topUpAmount) > 100}
                    className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {topUpLoading ? 'Redirecting...' : `Pay with Stripe (${topUpCurrency})`}
                  </button>
                </div>
                {isAdmin && (
                  <div className="flex justify-center">
                    <button
                      onClick={() => setShowManualTopUp(!showManualTopUp)}
                      className="text-sm text-gray-500 hover:text-gray-700 underline"
                    >
                      {showManualTopUp ? 'Hide' : 'Show'} manual top-up (admin only)
                    </button>
                  </div>
                )}
                {isAdmin && showManualTopUp && (
                  <div className="flex justify-end">
                    <button
                      onClick={handleManualTopUp}
                      disabled={topUpLoading || !topUpAmount}
                      className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                    >
                      {topUpLoading ? 'Adding...' : 'Manual Add (Testing)'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transaction History */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Transaction History
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Complete history of your credit transactions
          </p>
        </div>
        {transactions.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No transactions yet.</p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {transactions.map((transaction) => (
              <li key={transaction.id} className="px-4 py-4 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      {getTransactionIcon(transaction.type)}
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">
                        {transaction.description}
                      </div>
                      <div className="text-sm text-gray-500">
                        {new Date(transaction.timestamp).toLocaleString()}
                      </div>
                      {transaction.type === 'usage' && transaction.apiKeyId && (
                        <div className="text-xs text-gray-400">
                          API Key: {transaction.apiKeyId.substring(0, 8)}...
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center">
                    <div className={`text-sm font-medium ${getTransactionColor(transaction.type, transaction.amount)}`}>
                      {transaction.amount > 0 ? '+' : ''}${Math.abs(transaction.amount).toFixed(4)}
                    </div>
                    <div className="ml-2">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        transaction.type === 'topup' ? 'bg-green-100 text-green-800' :
                        transaction.type === 'usage' ? 'bg-red-100 text-red-800' :
                        transaction.type === 'refund' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {transaction.type.charAt(0).toUpperCase() + transaction.type.slice(1)}
                      </span>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
